import { useEffect } from "react";

/**
 * Sets document <title>, meta description, canonical URL, hreflang link tags,
 * and any Schema.org JSON-LD blocks for the page.
 *
 * @param {object} cfg
 * @param {string} cfg.title
 * @param {string} cfg.description
 * @param {string} cfg.path                  e.g. "/" or "/services"
 * @param {"en"|"de"} cfg.lang
 * @param {{en?: string, de?: string}} cfg.alternates  hreflang siblings (paths without origin)
 * @param {Array<object>} [cfg.jsonLd]       Array of Schema.org JSON-LD objects to inject
 */
export function useSeo({ title, description, path, lang, alternates = {}, jsonLd = [] }) {
  useEffect(() => {
    // Canonical hostname is always non-www muconcierge.com in production.
    // In preview/dev we fall back to current origin to keep links clickable.
    let origin = "https://muconcierge.com";
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.endsWith("muconcierge.com")) {
        origin = "https://muconcierge.com"; // strip www. and force non-www
      } else {
        origin = window.location.origin;
      }
    }

    document.title = title;
    document.documentElement.lang = lang;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(Object.keys(attrs)[0] === "rel" ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    setMeta(`meta[name="description"]`, { name: "description", content: description });
    setMeta(`meta[property="og:title"]`, { property: "og:title", content: title });
    setMeta(`meta[property="og:description"]`, { property: "og:description", content: description });
    setMeta(`meta[property="og:url"]`, { property: "og:url", content: origin + path });
    setMeta(`meta[property="og:locale"]`, { property: "og:locale", content: lang === "de" ? "de_DE" : "en_US" });
    setMeta(`meta[property="og:type"]`, { property: "og:type", content: "website" });
    setMeta(`meta[property="og:site_name"]`, { property: "og:site_name", content: "MUConcierge" });
    setMeta(`meta[name="twitter:card"]`, { name: "twitter:card", content: "summary_large_image" });
    setMeta(`meta[name="twitter:title"]`, { name: "twitter:title", content: title });
    setMeta(`meta[name="twitter:description"]`, { name: "twitter:description", content: description });
    setMeta(`link[rel="canonical"]`, { rel: "canonical", href: origin + path });

    // hreflang alternates — purge any old ones first
    document.head.querySelectorAll("link[data-hreflang]").forEach((el) => el.remove());
    const langs = { en: alternates.en, de: alternates.de };
    Object.entries(langs).forEach(([l, p]) => {
      if (!p) return;
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", l);
      link.setAttribute("href", origin + p);
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    });
    if (alternates.en) {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", "x-default");
      link.setAttribute("href", origin + alternates.en);
      link.setAttribute("data-hreflang", "true");
      document.head.appendChild(link);
    }

    // Schema.org JSON-LD blocks — always include the LocalBusiness root + page-specific
    document.head.querySelectorAll("script[data-jsonld]").forEach((el) => el.remove());
    const blocks = [buildOrganizationJsonLd(origin), ...jsonLd];
    blocks.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-jsonld", "true");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, lang, alternates.en, alternates.de, JSON.stringify(jsonLd)]);
}

/** Root organization / LocalBusiness schema present on every page. */
function buildOrganizationJsonLd(origin) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${origin}/#organization`,
    name: "MUConcierge",
    legalName: "MUConcierge",
    url: origin,
    email: "hello@muconcierge.com",
    telephone: "+49 176 6170 4157",
    image: `${origin}/og-image.jpg`,
    description:
      "MUConcierge: end-to-end furniture assembly service for expats in Munich. We assemble your furniture professionally and fast, so that you can move into your new apartment stress-free.",
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Munich",
      addressRegion: "Bavaria",
      addressCountry: "DE",
    },
    areaServed: {
      "@type": "City",
      name: "Munich",
    },
    sameAs: [],
    serviceType: [
      "Furniture assembly",
      "IKEA assembly",
      "Apartment setup",
      "Relocation concierge",
      "Move-in services",
    ],
  };
}

/** Helper to build a FAQPage schema from a Q&A array. */
export const faqPageSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

/** Helper to build a Service schema. */
export const serviceSchema = ({ name, description, areaServed = "Munich", price, url, provider = "MUConcierge" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  areaServed: { "@type": "City", name: areaServed },
  provider: { "@type": "LocalBusiness", name: provider },
  ...(url ? { url } : {}),
  ...(price ? { offers: { "@type": "Offer", price, priceCurrency: "EUR" } } : {}),
});

/** Helper to build BreadcrumbList schema. */
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});
